import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { VideoCamera, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "./ui/button";

export const fmtMeetingTime = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
};

export default function MyMeetingsCard() {
  const [meetings, setMeetings] = useState(null);
  const [attestFor, setAttestFor] = useState(null);
  const [typed, setTyped] = useState("");
  const [me, setMe] = useState(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const load = () => api.get("/meetings").then((r) => setMeetings(r.data.meetings || [])).catch(() => setMeetings([]));
  useEffect(() => {
    load();
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() => {});
  }, []);

  const sign = async () => {
    setBusy(true);
    try {
      await api.post(`/meetings/${attestFor.id}/attest`, { name: typed });
      toast.success("Attestation signed — your interactive training credit is final");
      setAttestFor(null);
      setTyped("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Name didn't match — type your full legal name");
    } finally { setBusy(false); }
  };

  if (!meetings) return null;
  const pendingAttest = meetings.filter((m) => m.pending_attestation);
  const todayKey = new Date().toISOString().slice(0, 10);
  const upcoming = meetings
    .filter((m) => m.status !== "canceled" && (m.start_at || "") >= todayKey)
    .sort((a, b) => (a.start_at || "").localeCompare(b.start_at || ""))
    .slice(0, 3);
  if (upcoming.length === 0 && pendingAttest.length === 0) return null;
  return (
    <div className="mb-6 rounded-xl border border-stone-200 bg-white p-6 shadow-sm" data-testid="my-meetings-card">
      {pendingAttest.length > 0 && (
        <div className="mb-3 rounded-lg border border-[#B7791F]/40 bg-[#B7791F]/10 p-3" data-testid="attest-needed">
          <p className="text-xs font-semibold text-[#8a5d14]">One more step — sign your training attestation</p>
          {pendingAttest.map((m) => (
            <div key={m.id} className="mt-2 flex flex-wrap items-center justify-between gap-2" data-testid={`attest-row-${m.id}`}>
              <p className="text-sm text-stone-800">{m.title} <span className="text-xs text-stone-500">· live interactive session · {fmtMeetingTime(m.start_at)}</span></p>
              <Button size="sm" onClick={() => setAttestFor(m)} className="rounded-full bg-[#8a5d14] text-white hover:bg-stone-900" data-testid={`attest-open-${m.id}`}>Sign attestation</Button>
            </div>
          ))}
        </div>
      )}
      {upcoming.length > 0 && (<>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="font-heading text-sm font-semibold text-stone-900">Upcoming meetings</p>
          <p className="mt-0.5 text-xs text-stone-500">Join from here when it's time — camera and mic run right in your browser.</p>
        </div>
        <Button size="sm" variant="outline" onClick={() => navigate("/meetings")} className="rounded-full border-stone-300" data-testid="my-meetings-all">
          All meetings <ArrowRight size={13} />
        </Button>
      </div>
      <div className="mt-3 divide-y divide-stone-100">
        {upcoming.map((m) => (
          <div key={m.id} className="flex flex-wrap items-center justify-between gap-2 py-2.5" data-testid={`my-meeting-${m.id}`}>
            <div>
              <p className="text-sm font-medium text-stone-800">
                {m.title}
                {m.kind === "training" && <span className="ml-2 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold text-primary">IN-SERVICE</span>}
                {m.is_orientation && <span className="ml-2 rounded-full bg-[#27AE60]/10 px-2 py-0.5 text-[10px] font-bold text-[#1e8449]">WELCOME</span>}
              </p>
              <p className="text-xs text-stone-500">{fmtMeetingTime(m.start_at)} · {m.duration_minutes} min{m.location ? ` · ${m.location}` : ""}</p>
              {m.notes && (
                <details className="mt-1" data-testid={`my-meeting-agenda-${m.id}`}>
                  <summary className="cursor-pointer text-xs font-semibold text-primary">Agenda</summary>
                  <pre className="mt-1 whitespace-pre-wrap rounded-md bg-stone-50 p-3 font-sans text-xs leading-relaxed text-stone-600">{m.notes}</pre>
                </details>
              )}
            </div>
            <Button size="sm" onClick={() => navigate(`/meetings/${m.id}`)} className="rounded-full bg-primary text-primary-foreground hover:bg-stone-900" data-testid={`my-meeting-join-${m.id}`}>
              <VideoCamera size={14} className="mr-1" /> Join
            </Button>
          </div>
        ))}
      </div>
      </>)}

      {attestFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="attest-modal">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <p className="font-heading text-lg font-semibold text-stone-900">Training attestation</p>
            <p className="mt-2 rounded-md bg-stone-50 p-3 text-sm leading-relaxed text-stone-700">
              I attest that I attended and participated in the live interactive training session
              <strong> "{attestFor.title}"</strong> held by video meeting on {(attestFor.start_at || "").slice(0, 10)},
              and that the training credit recorded for me is accurate.
            </p>
            <p className="mt-3 text-xs text-stone-500">Type your full legal name to sign: <strong>{me?.name}</strong></p>
            <input value={typed} onChange={(e) => setTyped(e.target.value)}
              className="mt-1.5 w-full rounded-md border border-stone-300 px-3 py-2 text-sm" placeholder="Your full legal name" data-testid="attest-name-input" />
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setAttestFor(null)} className="border-stone-300" data-testid="attest-cancel">Not now</Button>
              <Button onClick={sign} disabled={busy} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="attest-sign">
                {busy ? "Signing…" : "Sign attestation"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
